"use client";

import { c as _c } from "react/compiler-runtime";
import { zodResolver } from "@hookform/resolvers/zod";
import Image from "next/image";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { proposalFieldClassName } from "@/app/solicitar-proposta/_components/proposal-form-constants";
import { ButtonRadialFill } from "@/components/button-radial-fill";
import { usePageTransition } from "@/components/page-transition-provider";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { type ContactFormValues, contactFormSchema, contactOriginLabel } from "@/lib/contact";
import { formatPhone } from "@/lib/proposal";
const trackingKeys = ["utm_source", "utm_medium", "utm_campaign", "utm_content", "utm_term"] as const;
function readTrackingData(defaultOrigin: string) {
  if (typeof window === "undefined") {
    return {};
  }
  const fallbackOrigin = sessionStorage.getItem("origin") ?? defaultOrigin;
  return {
    origin: fallbackOrigin,
    utm_source: sessionStorage.getItem("utm_source") ?? "",
    utm_medium: sessionStorage.getItem("utm_medium") ?? "",
    utm_campaign: sessionStorage.getItem("utm_campaign") ?? "",
    utm_content: sessionStorage.getItem("utm_content") ?? "",
    utm_term: sessionStorage.getItem("utm_term") ?? ""
  };
}
export type ContactFormProps = {
  /** Rotula a origem do lead no HubSpot/e-mail quando não vem `?origin=` na URL. */
  origin?: string;
  /** Placeholder do campo de mensagem — as LPs pedem um contexto mais específico. */
  messagePlaceholder?: string;
};
export function ContactForm(t0) {
  const $ = _c(58);
  const {
    origin: t1,
    messagePlaceholder: t2
  } = t0;
  const origin = t1 === undefined ? contactOriginLabel : t1;
  const messagePlaceholder = t2 === undefined ? "Mensagem" : t2;
  const {
    navigate
  } = usePageTransition();
  const [feedback, setFeedback] = useState(null);
  let t3;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = {
      email: "",
      message: "",
      name: "",
      phone: ""
    };
    $[0] = t3;
  } else {
    t3 = $[0];
  }
  let t4;
  if ($[1] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = {
      defaultValues: t3,
      mode: "onTouched",
      reValidateMode: "onChange",
      resolver: zodResolver(contactFormSchema)
    };
    $[1] = t4;
  } else {
    t4 = $[1];
  }
  const {
    control,
    formState: t5,
    handleSubmit,
    register,
    reset
  } = useForm(t4);
  const {
    errors,
    isSubmitting
  } = t5;
  let t6;
  let t7;
  if ($[2] !== origin) {
    t6 = () => {
      if (typeof window === "undefined") {
        return;
      }
      const params = new URLSearchParams(window.location.search);
      for (const key of trackingKeys) {
        const value = params.get(key);
        if (value) {
          sessionStorage.setItem(key, value);
        }
      }
      sessionStorage.setItem("origin", params.get("origin") || origin);
    };
    t7 = [origin];
    $[2] = origin;
    $[3] = t6;
    $[4] = t7;
  } else {
    t6 = $[3];
    t7 = $[4];
  }
  useEffect(t6, t7);
  let t8;
  if ($[5] !== navigate || $[6] !== origin || $[7] !== reset) {
    t8 = async values => {
      setFeedback(null);
      const response = await fetch("/api/contact", {
        body: JSON.stringify({
          ...values,
          ...readTrackingData(origin)
        }),
        headers: {
          "Content-Type": "application/json"
        },
        method: "POST"
      });
      const data = (await response.json().catch(_temp)) as {
        message?: string;
      } | null;
      if (!response.ok) {
        setFeedback({
          status: "error",
          message: data?.message ?? "Erro ao enviar. Tente novamente mais tarde."
        });
        return;
      }
      reset();
      navigate("/contato-enviado");
    };
    $[5] = navigate;
    $[6] = origin;
    $[7] = reset;
    $[8] = t8;
  } else {
    t8 = $[8];
  }
  let t9;
  if ($[9] !== handleSubmit || $[10] !== t8) {
    t9 = handleSubmit(t8);
    $[9] = handleSubmit;
    $[10] = t8;
    $[11] = t9;
  } else {
    t9 = $[11];
  }
  const onSubmit = t9;
  let t10;
  if ($[12] === Symbol.for("react.memo_cache_sentinel")) {
    t10 = <label className="sr-only" htmlFor="contact-name">Seu nome</label>;
    $[12] = t10;
  } else {
    t10 = $[12];
  }
  let t11;
  if ($[13] !== register) {
    t11 = register("name");
    $[13] = register;
    $[14] = t11;
  } else {
    t11 = $[14];
  }
  let t12;
  if ($[15] !== t11) {
    t12 = <Input {...t11} className={proposalFieldClassName} id="contact-name" placeholder="Seu nome" />;
    $[15] = t11;
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  let t13;
  if ($[17] !== errors.name) {
    t13 = errors.name && <p className="mt-2 text-sm text-red-300">{errors.name.message}</p>;
    $[17] = errors.name;
    $[18] = t13;
  } else {
    t13 = $[18];
  }
  let t14;
  if ($[19] !== t12 || $[20] !== t13) {
    t14 = <div>{t10}{t12}{t13}</div>;
    $[19] = t12;
    $[20] = t13;
    $[21] = t14;
  } else {
    t14 = $[21];
  }
  let t15;
  if ($[22] === Symbol.for("react.memo_cache_sentinel")) {
    t15 = <label className="sr-only" htmlFor="contact-email">Seu email</label>;
    $[22] = t15;
  } else {
    t15 = $[22];
  }
  let t16;
  if ($[23] !== register) {
    t16 = register("email");
    $[23] = register;
    $[24] = t16;
  } else {
    t16 = $[24];
  }
  let t17;
  if ($[25] !== t16) {
    t17 = <Input {...t16} className={proposalFieldClassName} id="contact-email" placeholder="Seu email" type="email" />;
    $[25] = t16;
    $[26] = t17;
  } else {
    t17 = $[26];
  }
  let t18;
  if ($[27] !== errors.email) {
    t18 = errors.email && <p className="mt-2 text-sm text-red-300">{errors.email.message}</p>;
    $[27] = errors.email;
    $[28] = t18;
  } else {
    t18 = $[28];
  }
  let t19;
  if ($[29] !== t17 || $[30] !== t18) {
    t19 = <div>{t15}{t17}{t18}</div>;
    $[29] = t17;
    $[30] = t18;
    $[31] = t19;
  } else {
    t19 = $[31];
  }
  let t20;
  if ($[32] !== control) {
    t20 = <Controller control={control} name="phone" render={_temp2} />;
    $[32] = control;
    $[33] = t20;
  } else {
    t20 = $[33];
  }
  let t21;
  if ($[34] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <label className="sr-only" htmlFor="contact-message">Mensagem</label>;
    $[34] = t21;
  } else {
    t21 = $[34];
  }
  let t22;
  if ($[35] !== register) {
    t22 = register("message");
    $[35] = register;
    $[36] = t22;
  } else {
    t22 = $[36];
  }
  let t23;
  if ($[37] !== messagePlaceholder || $[38] !== t22) {
    t23 = <Textarea {...t22} className="min-h-42 w-full resize-none rounded-lg border-0 bg-gray-350 px-5 py-4 font-manrope text-[14px] leading-6 text-white shadow-none placeholder:text-[#dcdcdc] focus-visible:ring-1 focus-visible:ring-white/15" id="contact-message" placeholder={messagePlaceholder} />;
    $[37] = messagePlaceholder;
    $[38] = t22;
    $[39] = t23;
  } else {
    t23 = $[39];
  }
  let t24;
  if ($[40] !== errors.message) {
    t24 = errors.message && <p className="mt-2 text-sm text-red-300">{errors.message.message}</p>;
    $[40] = errors.message;
    $[41] = t24;
  } else {
    t24 = $[41];
  }
  let t25;
  if ($[42] !== t23 || $[43] !== t24) {
    t25 = <div>{t21}{t23}{t24}</div>;
    $[42] = t23;
    $[43] = t24;
    $[44] = t25;
  } else {
    t25 = $[44];
  }
  const t26 = isSubmitting ? "Enviando mensagem" : "Enviar mensagem";
  let t27;
  if ($[45] !== isSubmitting || $[46] !== t26) {
    t27 = <div className="pt-4"><ButtonRadialFill className="h-12 rounded-2xl border border-primary-300 bg-primary-300 px-8 text-base font-medium text-white" classNameFill="bg-gray-1100" disabled={isSubmitting} type="submit">{t26}</ButtonRadialFill></div>;
    $[45] = isSubmitting;
    $[46] = t26;
    $[47] = t27;
  } else {
    t27 = $[47];
  }
  let t28;
  if ($[48] !== feedback) {
    t28 = feedback && <p aria-live="polite" className={feedback.status === "success" ? "text-sm text-emerald-300" : "text-sm text-red-300"}>{feedback.message}</p>;
    $[48] = feedback;
    $[49] = t28;
  } else {
    t28 = $[49];
  }
  let t29;
  if ($[50] !== onSubmit || $[51] !== t14 || $[52] !== t19 || $[53] !== t20 || $[54] !== t25 || $[55] !== t27 || $[56] !== t28) {
    t29 = <form className="space-y-4" onSubmit={onSubmit}>{t14}{t19}{t20}{t25}{t27}{t28}</form>;
    $[50] = onSubmit;
    $[51] = t14;
    $[52] = t19;
    $[53] = t20;
    $[54] = t25;
    $[55] = t27;
    $[56] = t28;
    $[57] = t29;
  } else {
    t29 = $[57];
  }
  return t29;
}
function _temp2(t0) {
  const {
    field,
    fieldState
  } = t0;
  return <div><label className="sr-only" htmlFor="contact-phone">Qual seu telefone</label><div className="relative"><Input className={`${proposalFieldClassName} pr-14`} id="contact-phone" inputMode="numeric" onChange={event => field.onChange(formatPhone(event.target.value))} placeholder="Qual seu telefone" value={field.value ?? ""} /><Image alt="" aria-hidden="true" className="pointer-events-none absolute right-4 top-1/2 size-6 -translate-y-1/2 opacity-65 grayscale" height={24} src="/assets/socials/icon-whatsapp-green.svg" width={24} /></div>{fieldState.error && <p className="mt-2 text-sm text-red-300">{fieldState.error.message}</p>}</div>;
}
function _temp() {
  return null;
}